import { useMemo } from 'react';
import { useThemeMode } from './useThemeMode';

/**
 * Memoizes a StyleSheet factory, so styles are not recreated on every render
 * except when the resolved mode changes.
 *
 * The mode dependency exists for native. `Colors.role.*` there is a `Proxy`
 * (`roleTokens.ts`) that reads the current mode on each property access, so
 * *reading* it is already live; what is not live is a component that reads it
 * once and never renders again. Depending on `useThemeMode()` forces exactly
 * that re-render when the factory needs recomputing — the same problem
 * `themedStyleSheet` solves for module-scope sheets, solved here for the
 * component-scope case this hook exists for.
 *
 * On web this is mild, harmless waste rather than a needed behaviour: web's
 * `Colors.role.*` resolves to `var()` strings (`roleTokens.web.ts`) that never
 * change value on a flip (the browser resolves the variable, not this hook),
 * so `useThemeMode.web.ts`'s subscription still forces a recompute here, it
 * just produces an identical result. Left unconditional rather than branched
 * on platform: there are currently zero shared-ui or gripp-web consumers of
 * this hook to actually pay that cost. `useRoleHex` remains the answer for
 * the few colour-math boundaries (Paper props) that cannot take either.
 */
export function useThemedStyles<T>(styleCreator: () => T): T {
  const mode = useThemeMode();
  return useMemo(() => styleCreator(), [styleCreator, mode]);
}
